/**
 * Thanh AI dưới canvas: chọn model chat rồi mô tả bằng tiếng Việt, AI dựng /
 * sửa luồng ngay trên canvas. Phần AI chạy thật qua backend
 * (/automation-v2/ai/flow) — backend đã nạp lớp nền lược đồ workflow, kiểm tra
 * JSON trả về trước khi UI áp lên canvas; áp xong có thể Hoàn tác.
 */
import { useEffect, useRef, useState } from 'react'
import { Sparkles, SendHorizontal, Loader2, Undo2, AlertTriangle, ChevronDown, ChevronUp, Bot } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useAiConfig } from '@/hooks/use-ai'
import { useAiDungLuong, type LuotChatLuong } from '@/hooks/use-automation-v2-ai'
import { useStoreV2 } from '@/hooks/use-automation-v2'
import type { FlowEdgeV2, FlowNodeV2 } from '@/lib/automation-v2/types'
import { apiError } from '@/lib/api-client'

export interface LuongCanvas { name?: string; nodes: FlowNodeV2[]; edges: FlowEdgeV2[] }

const GOI_Y = [
  'Khách mua kháng sinh thì 2 ngày sau nhắn nhắc uống men vi sinh',
  'Đơn trên 2 triệu thì tạo việc cho quản lý gọi cảm ơn trong 4 giờ',
  'Thêm bước chờ 1 ngày rồi hỏi cảm nhận trước khi tặng voucher',
]

export function ThanhAiLuong({ layLuong, apLuong }: { layLuong: () => LuongCanvas; apLuong: (l: LuongCanvas) => void }) {
  const { data: cfg } = useAiConfig()
  const { connectors, mappings } = useStoreV2()
  const ai = useAiDungLuong()
  const [mo, setMo] = useState(false)
  const [nhap, setNhap] = useState('')
  const [chon, setChon] = useState('')           // "provider::model"
  const [hoiThoai, setHoiThoai] = useState<Array<LuotChatLuong & { thayDoi?: string[]; canhBao?: string }>>([])
  const [hoanTac, setHoanTac] = useState<LuongCanvas | null>(null)
  const cuonRef = useRef<HTMLDivElement>(null)

  const providers = cfg?.availableProviders ?? []
  const macDinh = cfg ? `${cfg.provider}::${cfg.model}` : ''
  const dangChon = chon || macDinh
  useEffect(() => { cuonRef.current?.scrollTo({ top: cuonRef.current.scrollHeight }) }, [hoiThoai, ai.isPending])

  const gui = (text?: string) => {
    const message = (text ?? nhap).trim()
    if (!message || ai.isPending) return
    const [provider, model] = dangChon.split('::')
    const truoc = layLuong()
    setHoiThoai((h) => [...h, { role: 'user', text: message }])
    setNhap('')
    ai.mutate(
      {
        message,
        luongHienTai: truoc,
        nguon: connectors.map((c) => ({ id: c.id, type: c.type, name: c.name })),
        anhXa: mappings.map((m) => ({ id: m.id, source: m.source, name: m.name })),
        provider: provider || null,
        model: model || null,
        lichSu: hoiThoai.slice(-6).map(({ role, text: t }) => ({ role, text: t })),
      },
      {
        onSuccess: (kq) => {
          setHoiThoai((h) => [...h, { role: 'assistant', text: kq.traLoi, thayDoi: kq.thayDoi, canhBao: kq.canhBao }])
          if (kq.luong?.nodes?.length) {
            setHoanTac(truoc)
            apLuong(kq.luong)
            toast.success(`AI đã cập nhật canvas (${kq.model})`)
          } else if (kq.canhBao) {
            toast.warning(kq.canhBao)
          }
        },
        onError: (err) => {
          const m = apiError(err)
          setHoiThoai((h) => [...h, { role: 'assistant', text: m, canhBao: m }])
          toast.error(m)
        },
      },
    )
  }

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-4 pb-4">
      <div className="pointer-events-auto w-full min-w-[20rem] max-w-3xl rounded-xl border bg-card/95 shadow-lg backdrop-blur">
        {/* Hội thoại */}
        {mo && (
          <div ref={cuonRef} className="max-h-64 space-y-2 overflow-y-auto border-b p-3 text-[12px]">
            {hoiThoai.length === 0 && (
              <div className="space-y-2">
                <p className="text-muted-foreground">Mô tả bằng tiếng Việt, AI dựng hoặc sửa luồng ngay trên canvas. Thử:</p>
                <div className="flex flex-wrap gap-1.5">
                  {GOI_Y.map((g) => <button key={g} type="button" className="rounded-full border px-2.5 py-1 text-[11px] hover:bg-accent" onClick={() => gui(g)}>{g}</button>)}
                </div>
              </div>
            )}
            {hoiThoai.map((t, i) => (
              <div key={i} className={cn('flex gap-2', t.role === 'user' && 'justify-end')}>
                {t.role === 'assistant' && <Bot className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
                <div className={cn('max-w-[85%] space-y-1 rounded-lg px-2.5 py-1.5', t.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted')}>
                  <p className="whitespace-pre-wrap leading-5">{t.text}</p>
                  {!!t.thayDoi?.length && <ul className="list-disc space-y-0.5 pl-4 text-[11px] opacity-80">{t.thayDoi.map((x, k) => <li key={k}>{x}</li>)}</ul>}
                  {t.canhBao && <p className="flex items-center gap-1 text-[11px] text-warning"><AlertTriangle className="h-3 w-3" /> {t.canhBao}</p>}
                </div>
              </div>
            ))}
            {ai.isPending && <p className="flex items-center gap-1.5 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> AI đang dựng luồng…</p>}
          </div>
        )}

        {/* Ô nhập: hàng chữ + hàng điều khiển, gọn ở mọi bề ngang */}
        <div className="space-y-1.5 p-2">
          <div className="flex items-start gap-2">
            <button type="button" className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-accent" title={mo ? 'Thu gọn hội thoại' : 'Mở hội thoại'} onClick={() => setMo((v) => !v)}>
              {mo ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
            <Sparkles className="mt-2 h-4 w-4 shrink-0 text-primary" />
            <Textarea
              rows={1} value={nhap} onFocus={() => setMo(true)}
              onChange={(e) => setNhap(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); gui() } }}
              placeholder="Nhờ AI dựng luồng, vd: “khách mua kháng sinh thì 2 ngày sau nhắn nhắc men vi sinh” (Enter để gửi)"
              className="max-h-28 min-h-[2.25rem] w-full min-w-0 flex-1 resize-none py-2 text-xs"
            />
          </div>
          <div className="flex items-center gap-2 pl-9">
            <Select value={dangChon} onValueChange={setChon}>
              <SelectTrigger className="h-7 w-48 shrink-0 text-[11px]" title="Model chat dùng để dựng luồng"><SelectValue placeholder="Chọn model" /></SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectGroup key={p.id}>
                    <SelectLabel className="text-[10.5px] uppercase text-muted-foreground">{p.name}</SelectLabel>
                    {p.models.map((m) => <SelectItem key={`${p.id}::${m.value}`} value={`${p.id}::${m.value}`}>{m.title}</SelectItem>)}
                  </SelectGroup>
                ))}
                {providers.length === 0 && macDinh && <SelectItem value={macDinh}>{cfg?.model}</SelectItem>}
              </SelectContent>
            </Select>
            <span className="hidden text-[10.5px] text-muted-foreground xl:inline">AI hiểu sẵn lược đồ luồng của hệ thống · chỉ sửa canvas, chưa lưu</span>
            <div className="ml-auto flex items-center gap-1.5">
              {hoanTac && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" title="Trả canvas về trước khi AI sửa"
                  onClick={() => { apLuong(hoanTac); setHoanTac(null); toast.success('Đã hoàn tác thay đổi của AI') }}>
                  <Undo2 className="h-3.5 w-3.5" /> Hoàn tác
                </Button>
              )}
              <Button size="sm" className="h-7 px-3" disabled={ai.isPending || !nhap.trim()} onClick={() => gui()}>
                {ai.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><SendHorizontal className="h-3.5 w-3.5" /> Gửi</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
